import React, { useState, useRef } from "react";
import Modal from "../../common/components/Modal";
import Input from "../../common/components/Input";
import BasicButton from "../../common/components/BasicButton";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import {
  useCreateAdminQuizSourceMaterialMutation,
  useFinalizeAdminQuizSourceMaterialUploadMutation,
  useRetryAdminQuizSourceMaterialUploadMutation,
} from "../../../lib/apollo/graphql/quiz/quiz-generated";

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload?: (title: string, file: File) => void;
  onUploadSuccess?: () => void;
}

export default function DocumentUploadModal({
  isOpen,
  onClose,
  onUpload,
  onUploadSuccess,
}: DocumentUploadModalProps) {
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createSourceMaterial] = useCreateAdminQuizSourceMaterialMutation();
  const [finalizeUpload] = useFinalizeAdminQuizSourceMaterialUploadMutation();
  const [retryUpload] = useRetryAdminQuizSourceMaterialUploadMutation();

  const handleFileSelect = (file: File) => {
    if (file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      alert("PDF 파일만 업로드 가능합니다.");
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert("문서명을 입력해주세요.");
      return;
    }
    if (!selectedFile) {
      alert("파일을 선택해주세요.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 1. Create source material and get upload URL
      const { data: createData } = await createSourceMaterial({
        variables: {
          input: {
            title: title.trim(),
            filename: selectedFile.name,
            contentType: selectedFile.type,
            fileSize: selectedFile.size,
          },
        },
      });

      if (!createData?.createSourceMaterial) {
        throw new Error("업로드 URL 생성에 실패했습니다.");
      }

      const { id, uploadUrl } = createData.createSourceMaterial;

      // 2. Upload file to GCS
      setUploadProgress(30);
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": selectedFile.type,
        },
        body: selectedFile,
      });

      if (!uploadResponse.ok) {
        throw new Error("파일 업로드에 실패했습니다.");
      }

      setUploadProgress(70);

      // 3. Finalize upload
      const { data: finalizeData } = await finalizeUpload({
        variables: {
          input: { id },
        },
      });

      if (!finalizeData?.finalizeSourceMaterialUpload) {
        throw new Error("업로드 완료 처리에 실패했습니다.");
      }

      setUploadProgress(100);
      alert("문서가 성공적으로 업로드되었습니다.");

      // Call legacy callback if provided
      onUpload?.(title, selectedFile);

      // Call new success callback
      onUploadSuccess?.();

      handleClose();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error instanceof Error ? error.message : "업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setTitle("");
    setSelectedFile(null);
    setIsDragOver(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      title="문서 업로드"
      width="clamp(600px, 33vw, 800px)"
      height="auto"
      onClose={handleClose}
    >
      <div className="space-y-6">
        {/* 문서명 입력 */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">문서명</label>
          <Input
            value={title}
            onValueChange={setTitle}
            placeholder="해당 문서에 대한 문서명을 입력하세요."
            className="w-full"
          />
        </div>

        {/* 파일 업로드 영역 */}
        <div className="space-y-2">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-blue-500 bg-blue-50"
                : selectedFile
                ? "border-green-500 bg-green-50"
                : "border-blue-400 bg-blue-50"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleUploadClick}
          >
            <div className="flex flex-col items-center space-y-3">
              <CloudArrowUpIcon
                className={`h-12 w-12 ${selectedFile ? 'text-green-500' : 'text-blue-500'}`}
              />
              {selectedFile ? (
                <div className="space-y-1">
                  <p className="text-green-600 font-medium">
                    선택된 파일: {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    다른 파일을 선택하려면 클릭하세요.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-blue-600 font-medium">
                    문서를 끌어다 놓거나 클릭하여 업로드하기
                  </p>
                  <p className="text-sm text-blue-500">(.pdf)</p>
                </div>
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {/* 업로드 진행 상태 */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>업로드 중...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* 버튼 영역 */}
        <div className="flex justify-end gap-3 pt-4">
          <BasicButton
            text="취소"
            variant="outlined"
            className="px-6 py-2 border-black text-black hover:bg-gray-100"
            onClick={handleClose}
            disabled={isUploading}
          />
          <BasicButton
            text={isUploading ? "업로드 중..." : "저장"}
            variant="primary"
            className="px-6 py-2 bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={isUploading}
          />
        </div>
      </div>
    </Modal>
  );
}